import React, { useState } from "react";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import { Link } from "react-router-dom";
import {
    CircularProgress,
    makeStyles,
    Button,
    TextField,
} from "@material-ui/core";
import { forgotPassword, verifyCode, resetPassword } from "../../actions/auth";
import Notify from "../customHelpers/Notify";
import Notification from "../customHelpers/Notification";
import logo from "../../assets/images/logo.png";

const useStyles = makeStyles((theme) => ({
    root: {
        height: "100vh",
        backgroundColor: "#f3f4f6",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    card: {
        maxWidth: 400,
        width: "100%",
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    },
    logoContainer: {
        display: "flex",
        justifyContent: "center",
        marginBottom: theme.spacing(3),
    },
    logo: {
        maxHeight: 80,
    },
    title: {
        fontWeight: 600,
        color: "rgb(20,61,89)",
        textAlign: "center",
        marginBottom: theme.spacing(1),
    },
    subtitle: {
        textAlign: "center",
        color: "#6b7280",
        marginBottom: theme.spacing(4),
    },
    submitButton: {
        marginTop: theme.spacing(3),
        backgroundColor: "rgba(244,151,3,.8)",
        color: "#fff",
        fontWeight: 600,
        "&:hover": {
            backgroundColor: "rgba(244,151,3, 1)",
        },
    },
    links: {
        marginTop: theme.spacing(3),
        display: "flex",
        justifyContent: "center",
    },
    linkText: {
        color: "rgb(20,61,89)",
        textDecoration: "none",
        fontSize: "0.875rem",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    stepIndicator: {
        textAlign: "center",
        marginBottom: theme.spacing(2),
        color: "rgba(244,151,3, 1)",
        fontWeight: "bold"
    }
}));

const ForgotPasswordComponent = () => {
    const classes = useStyles();
    const { NotifyMessage, notify, setNotify } = Notify();
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [step, setStep] = useState(1); // 1: Email, 2: Code, 3: Password
    const [loading, setLoading] = useState(false);

    const handleSendCode = (e) => {
        e.preventDefault();
        setLoading(true);
        forgotPassword(email).then((data) => {
            setLoading(false);
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
            } else {
                NotifyMessage({ message: data.message, type: "success" });
                setStep(2);
            }
        });
    };

    const handleVerifyCode = (e) => {
        e.preventDefault();
        setLoading(true);
        verifyCode(email, code).then((data) => {
            setLoading(false);
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
            } else {
                NotifyMessage({ message: data.message, type: "success" });
                setStep(3);
            }
        });
    };

    const handleResetPassword = (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            NotifyMessage({ message: "Passwords do not match", type: "error" });
            return;
        }
        setLoading(true);
        resetPassword({ email, code, newPassword }).then((data) => {
            setLoading(false);
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
            } else {
                NotifyMessage({ message: data.message, type: "success" });
                setStep(1);
                setEmail("");
                setCode("");
                setNewPassword("");
                setConfirmPassword("");
            }
        });
    };

    return (
        <div className={classes.root}>
            <Card className={classes.card}>
                <div className={classes.logoContainer}>
                    <img src={logo} className={classes.logo} alt="Logo" />
                </div>
                <CardContent>
                    <Typography variant="h5" className={classes.title}>
                        Forgot Password
                    </Typography>
                    <Typography variant="body2" className={classes.subtitle}>
                        {step === 1 && "Enter your email to receive a verification code."}
                        {step === 2 && "Enter the 6-digit code sent to your email."}
                        {step === 3 && "Create a new secure password for your account."}
                    </Typography>

                    <Typography className={classes.stepIndicator}>
                        Step {step} of 3
                    </Typography>

                    {step === 1 && (
                        <form onSubmit={handleSendCode}>
                            <TextField
                                variant="outlined" margin="normal" required fullWidth
                                label="Email Address" value={email} onChange={(e) => setEmail(e.target.value)}
                            />
                            <Button type="submit" fullWidth variant="contained" className={classes.submitButton} disabled={loading}>
                                {loading ? <CircularProgress size={24} color="inherit" /> : "Send Code"}
                            </Button>
                        </form>
                    )}

                    {step === 2 && (
                        <form onSubmit={handleVerifyCode}>
                            <TextField
                                variant="outlined" margin="normal" required fullWidth
                                label="Verification Code" value={code} onChange={(e) => setCode(e.target.value)}
                                helperText="Check your email for the 6-digit code"
                            />
                            <Button type="submit" fullWidth variant="contained" className={classes.submitButton} disabled={loading}>
                                {loading ? <CircularProgress size={24} color="inherit" /> : "Verify Code"}
                            </Button>
                            <Button fullWidth style={{ marginTop: 10 }} onClick={() => setStep(1)}>Back</Button>
                        </form>
                    )}

                    {step === 3 && (
                        <form onSubmit={handleResetPassword}>
                            <TextField
                                variant="outlined" margin="normal" required fullWidth type="password"
                                label="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                            />
                            <TextField
                                variant="outlined" margin="normal" required fullWidth type="password"
                                label="Confirm New Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <Button type="submit" fullWidth variant="contained" className={classes.submitButton} disabled={loading}>
                                {loading ? <CircularProgress size={24} color="inherit" /> : "Update Password"}
                            </Button>
                        </form>
                    )}

                    <div className={classes.links}>
                        <Link to="/staff/signin" className={classes.linkText}>Back to Login</Link>
                    </div>
                </CardContent>
            </Card>
            <Notification notify={notify} setNotify={setNotify} />
        </div>
    );
};

export default ForgotPasswordComponent;
